import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  heroContent,
  differentials,
  services,
  partners,
  testimonials,
  siteConfig,
} from '../../data/mock';
import {
  Zap,
  Shield,
  Cog,
  BarChart3,
  Search,
  Globe,
  Server,
  Puzzle,
  Link as LinkIcon,
  Cloud,
  ShieldCheck,
  Lock,
  Workflow,
  ArrowRight,
  MessageCircle,
  Quote,
  ChevronRight,
  Sparkles,
} from 'lucide-react';

const iconMap = {
  Zap,
  Shield,
  Cog,
  BarChart3,
  Search,
  Globe,
  Server,
  Puzzle,
  Link: LinkIcon,
  Cloud,
  ShieldCheck,
  Lock,
  Workflow,
};

// Logo URL
const logoUrl = "https://customer-assets.emergentagent.com/job_aa40e8f2-db60-4704-ab8e-d5a0df164063/artifacts/mbipe7fe_Frame%203.svg";

export const HomePage = () => {
  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/${siteConfig.whatsapp}?text=${encodeURIComponent(siteConfig.whatsappMessage)}`,
      '_blank'
    );
  };

  return (
    <main className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#329780]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-[#329780]/5 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-[#329780]/5 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-[#329780]/10 rounded-full" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="space-y-8 animate-fade-in">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#329780]/10 border border-[#329780]/20">
                <Sparkles className="w-4 h-4 text-[#329780] mr-2" />
                <span className="text-[#329780] text-sm font-medium">
                  Soluções em Tecnologia
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                {heroContent.title.split('.').map((part, i) => (
                  <span key={i}>
                    {part.trim()}
                    {i < heroContent.title.split('.').length - 1 && (
                      <span className="text-[#329780]">.</span>
                    )}
                    {i < heroContent.title.split('.').length - 2 && <br />}
                  </span>
                ))}
              </h1>

              <p className="text-lg text-slate-400 max-w-xl leading-relaxed">
                {heroContent.subtitle}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contato">
                  <Button
                    size="lg"
                    className="bg-[#329780] hover:bg-[#287a68] text-white px-8 h-12 text-base font-medium transition-all duration-200 hover:shadow-lg hover:shadow-[#329780]/20"
                  >
                    {heroContent.ctaPrimary}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleWhatsApp}
                  className="border-slate-700 text-slate-300 hover:bg-[#25D366]/10 hover:border-[#25D366]/50 hover:text-[#25D366] px-8 h-12 text-base font-medium transition-all duration-200"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  {heroContent.ctaSecondary}
                </Button>
              </div>
            </div>

            {/* Hero Visual */}
            <div className="relative hidden lg:block animate-fade-in delay-300">
              <div className="relative w-full aspect-square max-w-lg mx-auto">
                {/* Floating Cards */}
                <div className="absolute top-0 right-0 w-48 bg-[#111920] border border-[#1e293b] rounded-xl p-4 shadow-2xl shadow-black/30 animate-float">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-[#329780]/20 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-[#329780]" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Segurança</p>
                      <p className="text-[#329780] text-xs">99.9% Uptime</p>
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-20 left-0 w-52 bg-[#111920] border border-[#1e293b] rounded-xl p-4 shadow-2xl shadow-black/30 animate-float delay-500">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-[#329780]/20 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-[#329780]" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Automação</p>
                      <p className="text-[#329780] text-xs">+40% Eficiência</p>
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-0 right-10 w-56 bg-[#111920] border border-[#1e293b] rounded-xl p-4 shadow-2xl shadow-black/30 animate-float delay-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-[#329780]/20 flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-[#329780]" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Monitoramento</p>
                      <p className="text-[#329780] text-xs">24/7 Ativo</p>
                    </div>
                  </div>
                </div>

                {/* Center Element - Logo */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-2xl bg-white flex items-center justify-center shadow-2xl shadow-[#329780]/30 p-4">
                  <img 
                    src={logoUrl} 
                    alt="KC Byte iT" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Differentials Section */}
      <section className="py-24 bg-[#080c10]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Por que escolher a <span className="text-[#329780]">KC Byte iT</span>?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Oferecemos soluções que combinam expertise técnica com visão estratégica
              para impulsionar seu negócio.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {differentials.map((item, index) => {
              const IconComponent = iconMap[item.icon];
              return (
                <Card
                  key={item.id}
                  className="bg-[#111920] border-[#1e293b] card-hover group"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CardContent className="p-6 space-y-4">
                    <div className="w-12 h-12 rounded-xl bg-[#329780]/10 flex items-center justify-center group-hover:bg-[#329780]/20 transition-colors duration-300">
                      {IconComponent && (
                        <IconComponent className="w-6 h-6 text-[#329780]" />
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-white">
                      {item.title}
                    </h3>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Nossos <span className="text-[#329780]">Serviços</span>
              </h2>
              <p className="text-slate-400 max-w-xl">
                Soluções completas em tecnologia para atender todas as demandas da sua
                empresa.
              </p>
            </div>
            <Link to="/servicos" className="mt-6 lg:mt-0">
              <Button
                variant="outline"
                className="border-[#329780]/50 text-[#329780] hover:bg-[#329780]/10"
              >
                Ver todos os serviços
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 6).map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card
                  key={service.id}
                  className="bg-[#111920] border-[#1e293b] card-hover group overflow-hidden"
                >
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="w-12 h-12 rounded-xl bg-[#329780]/10 flex items-center justify-center group-hover:bg-[#329780]/20 transition-colors duration-300">
                        {IconComponent && (
                          <IconComponent className="w-6 h-6 text-[#329780]" />
                        )}
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-600 group-hover:text-[#329780] group-hover:translate-x-1 transition-all duration-300" />
                    </div>
                    <h3 className="text-lg font-semibold text-white">
                      {service.title}
                    </h3>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      {service.description}
                    </p>
                    <p className="text-[#329780] text-sm font-medium">
                      {service.benefit}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20 bg-[#080c10]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Parceiros e Tecnologias
            </h2>
            <p className="text-slate-400">
              Trabalhamos com as melhores soluções do mercado
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-16">
            {partners.map((partner) => (
              <div
                key={partner.id}
                className="flex items-center justify-center px-6 py-4 rounded-xl bg-[#111920]/50 border border-[#1e293b] hover:border-[#329780]/30 transition-colors duration-300"
              >
                <span className="text-slate-400 font-medium text-lg">
                  {partner.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              O que nossos <span className="text-[#329780]">clientes</span> dizem
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              A satisfação dos nossos clientes é o nosso maior reconhecimento.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card
                key={testimonial.id}
                className="bg-[#111920] border-[#1e293b] card-hover"
              >
                <CardContent className="p-6 space-y-6">
                  <Quote className="w-10 h-10 text-[#329780]/30" />
                  <p className="text-slate-300 leading-relaxed">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-[#329780]/20 flex items-center justify-center">
                      <span className="text-[#329780] font-semibold text-lg">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-white font-medium">{testimonial.name}</p>
                      <p className="text-slate-500 text-sm">
                        {testimonial.role} • {testimonial.company}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#080c10]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-[#111920] border border-[#1e293b] rounded-3xl p-8 sm:p-12 lg:p-16 relative overflow-hidden">
            {/* Background Glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#329780]/10 rounded-full blur-3xl" />

            <div className="relative space-y-6">
              <h2 className="text-3xl sm:text-4xl font-bold text-white">
                Pronto para transformar sua TI?
              </h2>
              <p className="text-slate-400 max-w-xl mx-auto">
                Entre em contato conosco e descubra como podemos ajudar sua empresa a
                alcançar novos patamares de eficiência e segurança.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                <Link to="/contato">
                  <Button
                    size="lg"
                    className="bg-[#329780] hover:bg-[#287a68] text-white px-8 h-12 text-base font-medium"
                  >
                    Solicitar Orçamento
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleWhatsApp}
                  className="border-slate-700 text-slate-300 hover:bg-white/5 px-8 h-12 text-base font-medium"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Falar no WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
